// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	RoomEvent,
	Track,
	type RemoteAudioTrack,
	type RemoteParticipant,
	type RemoteTrack,
	type RemoteTrackPublication,
	type Room,
} from 'livekit-client';
import {reaction} from 'mobx';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getSharedVoiceAudioContext} from '@app/features/voice/engine/VoiceSharedAudioContext';
import {isScreenShareAudioPublicationLike} from '@app/features/voice/engine/VoiceTrackSource';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {
	detectWasmSimdSupport,
	isNoiseSuppressionBackendSupported,
	type VoiceNoiseSuppressionBackend,
} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionBackends';
import {
	createNoiseSuppressionPluginNode,
	type NoiseSuppressionPluginNode,
} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionChain';
import {readEffectiveNoiseSuppression} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionRuntime';
import {
	createRemoteDeepFilterNode,
	prefetchRemoteDeepFilterAssets,
	type RemoteDeepFilterNode,
} from '@app/features/voice/utils/noise_suppression/RemoteDeepFilterNode';

const logger = new Logger('RemoteAudioNoiseSuppression');

export const REMOTE_NOISE_SUPPRESSION_CHOICES: ReadonlyArray<VoiceNoiseSuppressionBackend> = [
	'none',
	'gate',
	'speex',
	'rnnoise',
	'gtcrn',
	'deep_filter',
];

export function resolveRemoteNoiseSuppressionBackend(
	value: VoiceNoiseSuppressionBackend | null | undefined,
): VoiceNoiseSuppressionBackend {
	if (value != null && (REMOTE_NOISE_SUPPRESSION_CHOICES as ReadonlyArray<string>).includes(value)) {
		return value;
	}
	return 'none';
}

export function getSelectedRemoteNoiseSuppressionChoice(): VoiceNoiseSuppressionBackend {
	return resolveRemoteNoiseSuppressionBackend(VoiceSettings.getRemoteNoiseSuppressionBackend());
}

export function setRemoteNoiseSuppressionChoice(backend: VoiceNoiseSuppressionBackend): void {
	if (!(REMOTE_NOISE_SUPPRESSION_CHOICES as ReadonlyArray<string>).includes(backend)) return;
	VoiceSettings.remoteNoiseSuppressionBackend = backend;
}

type RemoteProcessingChain = NoiseSuppressionPluginNode | RemoteDeepFilterNode;

interface AttachedRemoteChain {
	backend: VoiceNoiseSuppressionBackend;
	chain: RemoteProcessingChain;
}

export class RemoteAudioNoiseSuppressionManager {
	private room: Room | null = null;
	private generation = 0;
	private readonly attached = new Map<RemoteAudioTrack, AttachedRemoteChain>();
	private readonly failedBackends = new Map<VoiceNoiseSuppressionBackend, string>();
	private readonly roomListenerDisposers: Array<() => void> = [];
	private readonly settingsDisposer: () => void;

	constructor() {
		this.settingsDisposer = reaction(
			() => getSelectedRemoteNoiseSuppressionChoice(),
			(backend) => {
				this.failedBackends.delete(backend);
				if (backend === 'deep_filter') prefetchRemoteDeepFilterAssets();
				this.refresh();
			},
		);
	}

	setRoom(room: Room | null): void {
		if (this.room === room) return;
		this.detachRoom();
		this.room = room;
		if (!room) return;
		const onSubscribed = (
			track: RemoteTrack,
			publication: RemoteTrackPublication,
			_participant: RemoteParticipant,
		) => {
			if (!this.isEligible(track, publication)) return;
			this.ensure(track as RemoteAudioTrack);
		};
		const onUnsubscribed = (
			track: RemoteTrack,
			_publication: RemoteTrackPublication,
			_participant: RemoteParticipant,
		) => {
			if (track.kind !== Track.Kind.Audio) return;
			this.release(track as RemoteAudioTrack);
		};
		room.on(RoomEvent.TrackSubscribed, onSubscribed);
		room.on(RoomEvent.TrackUnsubscribed, onUnsubscribed);
		this.roomListenerDisposers.push(() => {
			room.off(RoomEvent.TrackSubscribed, onSubscribed);
			room.off(RoomEvent.TrackUnsubscribed, onUnsubscribed);
		});
		this.applyToExistingTracks();
	}

	private detachRoom(): void {
		for (const dispose of this.roomListenerDisposers.splice(0)) dispose();
		this.room = null;
		this.generation += 1;
		for (const track of [...this.attached.keys()]) this.release(track);
		this.failedBackends.clear();
	}

	private applyToExistingTracks(): void {
		if (!this.room) return;
		for (const participant of this.room.remoteParticipants.values()) {
			for (const publication of participant.audioTrackPublications.values()) {
				const track = publication.audioTrack;
				if (track && this.isEligible(track, publication)) this.ensure(track);
			}
		}
	}

	private isEligible(track: RemoteTrack, publication: RemoteTrackPublication): boolean {
		return track.kind === Track.Kind.Audio && !isScreenShareAudioPublicationLike(publication);
	}

	private ensure(track: RemoteAudioTrack): void {
		const backend = getSelectedRemoteNoiseSuppressionChoice();
		const current = this.attached.get(track);
		if (backend === 'none') {
			if (current) this.release(track);
			return;
		}
		if (current?.backend === backend) return;
		if (this.failedBackends.has(backend)) return;
		if (current) this.release(track);
		const context = getSharedVoiceAudioContext();
		if (!context) {
			logger.warn('Incoming-audio noise suppression unavailable: shared voice audio context missing', {
				backend,
			});
			return;
		}
		if (
			!isNoiseSuppressionBackendSupported(backend, {
				sampleRate: context.sampleRate,
				wasmSimd: detectWasmSimdSupport(),
				audioWorklet: typeof context.audioWorklet !== 'undefined',
			})
		) {
			logger.warn('Incoming-audio noise suppression is not supported in this environment', {
				backend,
				sampleRate: context.sampleRate,
			});
			return;
		}
		const suppressionStrength = readEffectiveNoiseSuppression(context.sampleRate).suppressionStrength;
		const generation = this.generation;
		const room = this.room;
		void (async () => {
			try {
				const chain =
					backend === 'deep_filter'
						? await createRemoteDeepFilterNode(context)
						: await createNoiseSuppressionPluginNode({
								audioContext: context,
								backend,
								suppressionStrength,
							});
				if (generation !== this.generation || this.room !== room) {
					void chain.dispose();
					return;
				}
				if (this.attached.has(track)) {
					void chain.dispose();
					return;
				}
				this.attached.set(track, {backend, chain});
				track.setWebAudioPlugins([chain.node]);
				logger.info('Applied incoming-audio noise suppression', {backend, trackSid: track.sid});
			} catch (error) {
				const reason = error instanceof Error ? error.message : String(error);
				this.failedBackends.set(backend, reason);
				logger.warn('Incoming-audio noise suppression failed; leaving track unprocessed', {
					backend,
					error,
				});
			}
		})();
	}

	private release(track: RemoteAudioTrack): void {
		const attached = this.attached.get(track);
		if (!attached) return;
		this.attached.delete(track);
		try {
			track.setWebAudioPlugins([]);
		} catch (error) {
			logger.debug('Failed to clear incoming-audio noise suppression plugins', error);
		}
		void attached.chain.dispose();
	}

	refresh(): void {
		if (!this.room) return;
		this.generation += 1;
		for (const track of [...this.attached.keys()]) this.release(track);
		this.applyToExistingTracks();
	}

	dispose(): void {
		this.settingsDisposer();
		this.detachRoom();
	}
}

const remoteAudioNoiseSuppression = new RemoteAudioNoiseSuppressionManager();
export default remoteAudioNoiseSuppression;
